'use client';

import { supabase } from '@/services';
import { User } from '@supabase/supabase-js';
import { FC, ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { STORAGE_KEY } from '../constants';
import { useLocalStorage } from '../hooks';

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<{
  accessToken: string;
  isAuthenticated: boolean;
  user?: User;
  accessTokenState: string;
  setAccessTokenState: React.Dispatch<React.SetStateAction<string>>;
}>({
  accessToken: '',
  isAuthenticated: false,
  user: undefined,
  accessTokenState: '',
  setAccessTokenState: () => {},
});

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User>();
  // Access token is stored in local storage
  const [accessToken] = useLocalStorage(STORAGE_KEY.ACCESS_TOKEN, null);
  const isAuthenticated = !!accessToken;
  const [isHydrated, setIsHydrated] = useState(false);

  // Access token state to use for mfa check
  const [accessTokenState, setAccessTokenState] = useState('');

  // Handle hydration
  useEffect(() => {
    setIsHydrated(true);

    // Additional safeguard: Clear reb2b tracking data during auth initialization
    // This prevents any stale tracking data from interfering with post-login navigation
    if (typeof window !== 'undefined' && window.localStorage) {
      const reb2bKeys = ['_reb2bref', '_reb2bsessionID', '_reb2buid'];
      reb2bKeys.forEach((key) => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!accessToken || !isHydrated) return;
    handleGetUserInfo();
  }, [accessToken, isHydrated]);

  const handleGetUserInfo = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      setUser(user || undefined);
    } catch (error) {
      console.log(error);
    }
  };

  // Prevent hydration mismatch by using default values until client-side hydration
  const contextValue = {
    isAuthenticated: isHydrated ? isAuthenticated : false,
    accessToken: isHydrated ? accessToken : '',
    user: isHydrated ? user : undefined,
    accessTokenState,
    setAccessTokenState,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
